import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Music2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';

export default function PortalLogin() {
  const { login, register, checkEmailAllowed, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const role = await checkEmailAllowed(email.trim());
      if (!role) {
        setError("This email isn't registered. If you think this is a mistake, please contact us.");
        setLoading(false);
        return;
      }
      setUserRole(role);
      setStep('password');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email.trim(), password);
      navigate(result.role === 'admin' ? '/admin' : '/portal');
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        if (!isNewUser) {
          setIsNewUser(true);
          setError('No account found. Click "Create Account" to set up your password.');
        } else {
          try {
            const result = await register(email.trim(), password);
            navigate(result.role === 'admin' ? '/admin' : '/portal');
          } catch (regErr) {
            setError(regErr.message || 'Failed to create account.');
          }
        }
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again or reset your password.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    }
    setLoading(false);
  };

  const handleReset = async () => {
    setError('');
    try {
      await resetPassword(email.trim());
      setResetSent(true);
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
    }
  };

  return (
    <div data-testid="portal-login-page" className="min-h-[80vh] flex items-center justify-center bg-[#FAFAFA] py-16 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/logo.jpg" alt="Singvolve" className="h-12 w-auto" />
          </Link>
          <h1 className="font-heading text-3xl font-bold text-[#2D1B36]">Student Login</h1>
          <p className="text-sm text-[#8A7E90] mt-2">Sign in to access your lessons and progress</p>
        </div>

        <Card className="border border-purple-100 rounded-2xl shadow-lg">
          <CardContent className="p-8">
            {step === 'email' ? (
              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#2D1B36]">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="mt-1.5 h-12 rounded-lg border-gray-200 focus:border-[#4B1D5E] focus:ring-[#4B1D5E]"
                    data-testid="login-email-input"
                  />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-full py-5 font-semibold"
                  data-testid="login-continue-btn"
                >
                  {loading ? 'Checking...' : 'Continue'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handlePasswordSubmit} className="space-y-5">
                <button type="button" onClick={() => { setStep('email'); setError(''); setIsNewUser(false); }} className="flex items-center gap-1 text-sm text-[#4B1D5E] hover:underline mb-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <p className="text-sm text-[#5E4B66]">Signing in as <span className="font-medium text-[#2D1B36]">{email}</span></p>
                <div className="relative">
                  <Label htmlFor="password" className="text-sm font-medium text-[#2D1B36]">
                    {isNewUser ? 'Create a password' : 'Password'}
                  </Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={isNewUser ? 'Create a secure password' : 'Enter your password'}
                      required
                      minLength={6}
                      className="h-12 rounded-lg border-gray-200 focus:border-[#4B1D5E] focus:ring-[#4B1D5E] pr-10"
                      data-testid="login-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
                {resetSent && <p className="text-sm text-green-600">Password reset email sent! Check your inbox.</p>}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-full py-5 font-semibold"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Please wait...' : isNewUser ? 'Create Account' : 'Sign In'}
                </Button>
                {!isNewUser && (
                  <button type="button" onClick={handleReset} className="w-full text-center text-sm text-[#4B1D5E] hover:underline" data-testid="login-reset-btn">
                    Forgot your password?
                  </button>
                )}
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-[#8A7E90] mt-6">
          Don't have access? <Link to="/contact" className="text-[#4B1D5E] hover:underline">Contact us</Link> to enquire about lessons.
        </p>
      </motion.div>
    </div>
  );
}
